import { CallMapper } from '../../../../../common/calls/mappers/CallMapper';
import { Call } from '../../../../../common/calls/models/Call';
import { CALLS_FRAGMENT } from '../../../../../common/calls/services/CallRepository';
import { InvalidUrlError } from '../../../../../common/errors/errors';
import { LocationMapper } from '../../../../../common/locations/mappers/LocationMapper';
import { Location } from '../../../../../common/locations/models/Location';
import { LOCATIONS_FRAGMENT } from '../../../../../common/locations/services/LocationsService';
import { BaseGQLService } from '../../../../../common/services/BaseGQLService';
import { CursorInfo, PaginationResult } from '../../../../../common/types/pagination';
import { ModelType } from '../../../../../common/utils/GQLIdEncoder';
import { FeatureFlagsType, getFeatureFlag } from '../../../../../config/launch-darkly/FeatureFlagsController';
import { InstanceMapper } from '../mappers/InstanceMapper';
import { OperationMapper } from '../mappers/OperationMapper';
import { Instance } from '../models/Instance';
import { Operation } from '../models/Operation';

export const DEFAULT_INSTANCE_OPERATIONS_PAGE_SIZE = 5;
export const DEFAULT_INSTANCE_CALLS_PAGE_SIZE = 5;
export const DEFAULT_INSTANCE_LOCATIONS_PAGE_SIZE = 5;

const INSTANCE_FRAGMENT = `
id
severity
data
filterStatus
details
numberLocal
remediation {
  general {
    remediation
    triage
    specificRemediationAdvice
    link
  }
  specificTag
}
hardCodedStringLocations {
  class
  method
  line
  file
}
`;

const OPERATIONS_FRAGMENT = `
totalCount
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  node {
    id
    type
    context
    algorithms
    keys {
      edges {
        node {
          id
          shortDescription
        }
      }
    }
  }
}
`;

export const GET_INSTANCE_QUERY = `
query ($reportId: ID!, $instanceNumber: String, $includeJiraExportUrl: Boolean!, $includeServiceNowExportUrl: Boolean!) {
  node(id: $reportId) {
    ... on ReportDone {
      id
      traceType
      slot {
        project {
          id
        }
      }
      instances(filters: {field: number, operation: EQ, value: $instanceNumber}) {
        edges {
          node {
            rule {
              title
              shortExplanation
              number
            }
            ${INSTANCE_FRAGMENT}
            calls(first: ${DEFAULT_INSTANCE_CALLS_PAGE_SIZE}) {
              ${CALLS_FRAGMENT}
            }
            operations(first: ${DEFAULT_INSTANCE_OPERATIONS_PAGE_SIZE}) {
              ${OPERATIONS_FRAGMENT}
            }
            locations(first: ${DEFAULT_INSTANCE_LOCATIONS_PAGE_SIZE}) {
              ${LOCATIONS_FRAGMENT}
            }
            jiraExportUrl @include(if: $includeJiraExportUrl)
            serviceNowExportUrl @include(if: $includeServiceNowExportUrl)
          }
        }
      }
    }
  }
}
`;

export const GET_OPERATIONS_QUERY = `
query (
  $instanceId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
) {
  node(id: $instanceId) {
    ... on Instance {
      operations(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
      ) {
        ${OPERATIONS_FRAGMENT}
      }
    }
  }
}
`;

export class InstanceRepository extends BaseGQLService {
  private readonly instanceMapper = new InstanceMapper();
  private readonly operationMapper = new OperationMapper();
  private readonly locationsMapper = new LocationMapper();
  private readonly callMapper = new CallMapper();

  async getInstance(
    reportId: number,
    issueNumber: number,
    instanceNumber: number
  ): Promise<{
    instance: Instance;
    initialCalls: PaginationResult<Call>;
    initialOperations: PaginationResult<Operation>;
    initialLocations: PaginationResult<Location>;
  }> {
    return this.query(
      GET_INSTANCE_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        instanceNumber: instanceNumber.toString(),
        includeJiraExportUrl: Boolean(getFeatureFlag(FeatureFlagsType.ENABLE_JIRA_BATCH_EXPORT)),
        includeServiceNowExportUrl: Boolean(getFeatureFlag(FeatureFlagsType.ENABLE_SERVICE_NOW)),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Report ${reportId}`);
        }
        const { instances } = data.node;
        if (!instances.edges?.length) {
          throw new InvalidUrlError(`Invalid request: could not find Instance ${instanceNumber} in Report ${reportId}`);
        }
        const { calls, operations, locations, ...instance } = instances.edges[0].node;
        const { totalCount: callsTotalCount, pageInfo: callsPageInfo, edges: callsEdges } = calls;
        const { totalCount: operationsTotalCount, pageInfo: operationsPageInfo, edges: operationsEdges } = operations;
        const { totalCount: locationsTotalCount, pageInfo: locationsPageInfo, edges: locationsEdges } = locations;

        if (instance.rule.number !== issueNumber) {
          // We are retrieving the instance through its number, which is unique for a report.
          // If we navigate to a URL such as `/report/<valid_id>/<another_issue_number>/<valid_instance_number>`,
          // all the requests would still resolve correctly, but we would display the instance list for
          // <another_issue_number> while showing an instance that does not belong to that issue.
          // Enforce a consistency check and simulate an error on failure.
          throw new InvalidUrlError(`Invalid request: could not find Instance ${instanceNumber} in Rule ${issueNumber} in Report ${reportId}`);
        }

        return {
          instance: this.instanceMapper.mapGQLModel({
            ...instance,
            reportId: data.node.id,
            projectId: data.node.slot.project.id,
            traceType: data.node.traceType,
          }),
          initialCalls: {
            data: callsEdges.map(({ node }: any) => this.callMapper.mapGQLModel(node)),
            pageInfo: callsPageInfo,
            totalCount: callsTotalCount,
          },
          initialOperations: {
            data: operationsEdges.map(({ node }: any) => this.operationMapper.mapGQLModel(node)),
            pageInfo: operationsPageInfo,
            totalCount: operationsTotalCount,
          },
          initialLocations: {
            data: locationsEdges.map(({ node }: any) => this.locationsMapper.mapGQLModel(node)),
            pageInfo: locationsPageInfo,
            totalCount: locationsTotalCount,
          },
        };
      }
    );
  }

  async getInstanceOperations(instanceId: number, cursor: CursorInfo): Promise<PaginationResult<Operation>> {
    return this.query(
      GET_OPERATIONS_QUERY,
      {
        instanceId: this.gqlIdEncoder.encode(ModelType.Instance, instanceId),
        ...this.mapCursorInfo(cursor),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Instance ${instanceId}`);
        }
        const { edges, pageInfo, totalCount } = data.node.operations;
        return {
          data: edges.map(({ node }: any) => this.operationMapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
}
